// @flow

import type { State } from "@store/reducers";
import type { ReasonSpecificScript } from "@modules/service/reducer/reason-specific-scripts/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import { validateHierarchyAssignmentSchema } from "./index";

const { DRAFT_ONLY, ACTIVE_WITH_DRAFT } = ApprovalStateEnum;

export type GetReasonSpecificScriptsList = (state: State) => Array<ReasonSpecificScript>;

export const getReasonSpecificScriptsList = (
  state: State,
): Array<ApprovableContainer<ReasonSpecificScript>> =>
  (state.service.reasonSpecificScripts.list || {}).content || [];

export const getReasonSpecificScriptsActiveList: GetReasonSpecificScriptsList = (state: State) => {
  return getReasonSpecificScriptsList(state)
    .filter(c => c.approvalState.indexOf("ACTIVE_") !== -1)
    .reduce((acc, c) => (c.active ? [...acc, c.active] : acc), []);
};

export const getReasonSpecificScriptsPendingList: GetReasonSpecificScriptsList = (state: State) => {
  return getReasonSpecificScriptsList(state)
    .filter(c => c.approvalState.indexOf("PENDING_REVIEW") !== -1)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getReasonSpecificScriptsDraftList: GetReasonSpecificScriptsList = (state: State) => {
  return getReasonSpecificScriptsList(state)
    .filter(c => c.approvalState === DRAFT_ONLY || c.approvalState === ACTIVE_WITH_DRAFT)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getReasonSpecificScriptContainer = (
  state: State,
): ?ApprovableContainer<ReasonSpecificScript> => state.service.reasonSpecificScripts.current;

export const getReasonSpecificScriptDraft = (state: State): ?ReasonSpecificScript =>
  state.service.reasonSpecificScripts.draft;

export const getReasonSpecificScriptAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.reasonSpecificScripts.assignments;

export const getReasonSpecificScriptDraftAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.reasonSpecificScripts.draftAssignments;

export const isReasonSpecificScriptDraftAssignmentsValid = (state: State): boolean => {
  const assignments = getReasonSpecificScriptDraftAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const isReasonSpecificScriptAssignmentsValid = (state: State): boolean => {
  const assignments = getReasonSpecificScriptAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

// MODALS
export const getShowAddReasonSpecificScriptModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showAddReasonSpecificScriptModal;

export const getShowEditReasonSpecificScriptModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showEditModal;

export const getReasonSpecificScriptsModalStep = (state: State): number =>
  state.service.reasonSpecificScripts.modals.modalStep;

export const getShowReasonSpecificScriptSubmitModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showSubmitModal;

export const getShowReasonSpecificScriptCancelModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showCancelModal;

export const getShowReasonSpecificScriptDiscardModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showDiscardModal;

export const getShowReasonSpecificScriptCancelDiscardModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showCancelDiscardModal;

export const getShowReasonSpecificScriptDisableModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showDisableModal;

export const getShowReasonSpecificScriptEditAssignmentModal = (state: State): boolean =>
  state.service.reasonSpecificScripts.modals.showEditAssignmentsModal;
